import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Avatar,
  CircularProgress,
  Menu,
  MenuItem,
  Tooltip
} from "@mui/material";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import { useTheme as useThemeV5 } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import AddCircle from "@mui/icons-material/AddCircle";
import DevicesFold from "@mui/icons-material/DevicesFold";
import MoreVert from "@mui/icons-material/MoreVert";
import Build from "@mui/icons-material/Build";
import ContentCopy from "@mui/icons-material/ContentCopy";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../../components/ConfirmationModal";
import NewTicketModal from "../../components/NewTicketModal";
import FlowBuilderModal from "../../components/FlowBuilderModal";
import toastError from "../../errors/toastError";
import { Can } from "../../components/Can";
import { AuthContext } from "../../context/Auth/AuthContext";

// ---------------------------------------------------------------------------
// Global Styles (mesmo do Dashboard)
// ---------------------------------------------------------------------------
const globalStyles = `
  @keyframes pulse-ring {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.6; }
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 1; }
  }
`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = globalStyles;
  document.head.appendChild(styleSheet);
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_CONTACTS": {
      const contacts = action.payload;
      const newContacts = [];
      contacts.forEach((contact) => {
        const idx = state.findIndex((c) => c.id === contact.id);
        if (idx !== -1) {
          state[idx] = contact;
        } else {
          newContacts.push(contact);
        }
      });
      return [...state, ...newContacts];
    }
    case "UPDATE_CONTACTS": {
      const contact = action.payload;
      const idx = state.findIndex((c) => c.id === contact.id);
      if (idx !== -1) {
        state[idx] = contact;
        return [...state];
      }
      return [contact, ...state];
    }
    case "DELETE_CONTACT": {
      const contactId = action.payload;
      const idx = state.findIndex((c) => c.id === contactId);
      if (idx !== -1) state.splice(idx, 1);
      return [...state];
    }
    case "RESET":
      return [];
    default:
      return state;
  }
};

// ---------------------------------------------------------------------------
// FlowBuilder
// ---------------------------------------------------------------------------
const FlowBuilder = () => {
  const themeV5 = useThemeV5();
  const themeV4 = useThemeV4();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);

  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";

  // --- State ---
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [webhooks, setWebhooks] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedWebhookName, setSelectedWebhookName] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDuplicateOpen, setConfirmDuplicateOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [reloadData, setReloadData] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  // ---------------------------------------------------------------------------
  // Fetch
  // ---------------------------------------------------------------------------
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delay = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/flowbuilder");
          setWebhooks(data.flows);
          dispatch({ type: "LOAD_CONTACTS", payload: data.flows });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delay);
  }, [searchParam, pageNumber, reloadData]);

  // ---------------------------------------------------------------------------
  // Socket
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const companyId = user.companyId;
    const onContact = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
      }
    };
    socket.on(`company-${companyId}-contact`, onContact);
    return () => socket.disconnect();
  }, [socket, user.companyId]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const hadleEditContact = () => {
    setSelectedContactId(deletingContact.id);
    setSelectedWebhookName(deletingContact.name);
    setContactModalOpen(true);
  };

  const handleDeleteWebhook = async (webhookId) => {
    try {
      await api.delete(`/flowbuilder/${webhookId}`);
      toast.success("Fluxo excluído com sucesso");
      setReloadData((old) => !old);
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setConfirmOpen(false);
  };

  const handleDuplicateFlow = async (flowId) => {
    try {
      await api.post(`/flowbuilder/duplicate`, { flowId: flowId });
      toast.success("Fluxo duplicado com sucesso");
      setReloadData((old) => !old);
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setConfirmDuplicateOpen(false);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const exportLink = () => {
    history.push(`/flowbuilder/${deletingContact.id}`);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 3 },
        bgcolor: "#f8fafc"
      }}
    >
      <Container maxWidth="xl">

        {/* Header */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={3}
          sx={{ mb: 4 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                bgcolor: PRIMARY_MAIN,
                "& svg": { width: 28, height: 28, color: "#fff" }
              }}
            >
              <AccountTreeIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{
                  color: "text.primary",
                  mb: 0.5,
                  letterSpacing: "-0.5px",
                  fontSize: { xs: "1.5rem", md: "1.875rem" }
                }}
              >
                Fluxos de conversa
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.875rem" }}>
                Gerenciamento de fluxos automatizados
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={2}>
            {/* Search */}
            <TextField
              placeholder={i18n.t("contacts.searchPlaceholder")}
              type="search"
              size="small"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "text.disabled", fontSize: 20 }} />
                  </InputAdornment>
                )
              }}
              sx={{
                width: { xs: 200, sm: 280 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "#fff",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  "& fieldset": { border: "none" },
                  "&:hover": { 
                    borderColor: PRIMARY_MAIN,
                    bgcolor: "action.hover"
                  },
                  "&.Mui-focused": { 
                    borderColor: PRIMARY_MAIN,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)"
                  }
                },
                "& .MuiOutlinedInput-input": {
                  py: 1.25,
                  fontSize: "0.875rem",
                  fontWeight: 500
                }
              }}
            />

            {/* Add button */}
            <Button
              variant="contained"
              startIcon={<AddCircle />}
              onClick={handleOpenContactModal}
              sx={{
                bgcolor: PRIMARY_MAIN,
                color: "#fff",
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                "&:hover": {
                  bgcolor: PRIMARY_DARK,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.18)"
                }
              }}
            >
              Adicionar Fluxo
            </Button>
          </Stack>
        </Stack>

        {/* Conteúdo */}
        <Paper
          elevation={0}
          onScroll={handleScroll}
          sx={{
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden"
          }}
        >
          {/* Cabeçalho da tabela */}
          <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
                {searchParam ? `Resultados da busca` : "Todos os fluxos"}
              </Typography>
              <Chip
                label={`${webhooks.length} ${webhooks.length !== 1 ? "fluxos" : "fluxo"}`}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  bgcolor: "rgba(148, 163, 184, 0.08)",
                  color: "text.secondary",
                  border: "1px solid",
                  borderColor: "rgba(148, 163, 184, 0.2)"
                }}
              />
            </Stack>
          </Box>

          {/* Loading */}
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Lista de fluxos */}
          {!loading && (
            <Box sx={{ p: 3 }}>
              <Stack spacing={2}>
                {webhooks.map((contact) => (
                  <Paper
                    key={contact.id}
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      bgcolor: "#fff",
                      border: "1px solid",
                      borderColor: "divider",
                      transition: "all 0.2s",
                      cursor: "pointer",
                      "&:hover": {
                        borderColor: PRIMARY_MAIN,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                      }
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ p: 2.5 }}
                    >
                      {/* Nome */}
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{ flex: 1 }}
                        onClick={() => history.push(`/flowbuilder/${contact.id}`)}
                      >
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: `${PRIMARY_MAIN}10`,
                            color: PRIMARY_MAIN
                          }}
                        >
                          <DevicesFold sx={{ fontSize: 20 }} />
                        </Avatar>
                        <Typography variant="body1" fontWeight={600} sx={{ fontSize: "0.875rem" }}>
                          {contact.name}
                        </Typography>
                      </Stack>

                      {/* Status */}
                      <Box 
                        sx={{ px: 3 }}
                        onClick={() => history.push(`/flowbuilder/${contact.id}`)}
                      >
                        <Chip
                          icon={contact.active ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <CancelIcon sx={{ fontSize: 14 }} />}
                          label={contact.active ? "Ativo" : "Desativado"}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            bgcolor: contact.active ? "rgba(16, 185, 129, 0.08)" : "rgba(148, 163, 184, 0.08)",
                            color: contact.active ? "#10b981" : "text.secondary",
                            border: "1px solid",
                            borderColor: contact.active ? "rgba(16, 185, 129, 0.2)" : "rgba(148, 163, 184, 0.2)",
                            "& .MuiChip-icon": {
                              color: contact.active ? "#10b981" : "text.secondary"
                            }
                          }}
                        />
                      </Box>

                      {/* Ações */}
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Mais opções">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              handleClick(e);
                              setDeletingContact(contact);
                            }}
                            sx={{
                              color: "text.secondary",
                              "&:hover": {
                                color: PRIMARY_MAIN,
                                bgcolor: `${PRIMARY_MAIN}10`
                              }
                            }}
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Paper>
                ))}

                {/* Empty state */}
                {webhooks.length === 0 && (
                  <Box sx={{ textAlign: "center", py: 8 }}>
                    <AccountTreeIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2, opacity: 0.3 }} />
                    <Typography variant="h6" fontWeight={600} sx={{ color: "text.secondary", mb: 1 }}>
                      Nenhum fluxo criado
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.disabled" }}>
                      Clique no botão "Adicionar Fluxo" para criar seu primeiro fluxo
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          )}
        </Paper>
      </Container>

      {/* Menu de contexto */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            mt: 1,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            border: "1px solid",
            borderColor: "divider"
          }
        }}
      >
        <MenuItem
          onClick={() => {
            handleClose();
            hadleEditContact();
          }}
          sx={{ 
            fontWeight: 500, 
            fontSize: "0.875rem",
            "&:hover": { bgcolor: `${PRIMARY_MAIN}10` } 
          }}
        >
          <EditIcon sx={{ mr: 1.5, fontSize: 18, color: "text.secondary" }} />
          Editar nome
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            exportLink();
          }}
          sx={{ 
            fontWeight: 500,
            fontSize: "0.875rem",
            "&:hover": { bgcolor: `${PRIMARY_MAIN}10` } 
          }}
        >
          <Build sx={{ mr: 1.5, fontSize: 18, color: "text.secondary" }} />
          Editar fluxo
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setConfirmDuplicateOpen(true);
          }}
          sx={{ 
            fontWeight: 500,
            fontSize: "0.875rem",
            "&:hover": { bgcolor: `${PRIMARY_MAIN}10` } 
          }}
        >
          <ContentCopy sx={{ mr: 1.5, fontSize: 18, color: "text.secondary" }} />
          Duplicar
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setConfirmOpen(true);
          }}
          sx={{ 
            fontWeight: 500,
            fontSize: "0.875rem",
            "&:hover": { bgcolor: "rgba(239,68,68,0.08)" } 
          }}
        >
          <DeleteOutlineIcon sx={{ mr: 1.5, fontSize: 18, color: "error.main" }} />
          <Typography sx={{ color: "error.main", fontSize: "0.875rem" }}>Excluir</Typography>
        </MenuItem>
      </Menu>

      {/* Modais */}
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={(ticket) => handleCloseOrOpenTicket(ticket)}
      />

      <FlowBuilderModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        flowId={selectedContactId}
        nameWebhook={selectedWebhookName}
        onSave={() => setReloadData((old) => !old)}
      />

      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contacts.confirmationModal.deleteTitle")} ${deletingContact.name}?`
            : `${i18n.t("contacts.confirmationModal.importTitlte")}`
        }
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => (deletingContact ? handleDeleteWebhook(deletingContact.id) : null)}
      >
        {deletingContact
          ? `Tem certeza que deseja deletar este fluxo? Todas as integrações relacionadas serão perdidas.`
          : `${i18n.t("contacts.confirmationModal.importMessage")}`}
      </ConfirmationModal>

      <ConfirmationModal
        title={
          deletingContact
            ? `Deseja duplicar o fluxo ${deletingContact.name}?`
            : `${i18n.t("contacts.confirmationModal.importTitlte")}`
        }
        open={confirmDuplicateOpen}
        onClose={() => setConfirmDuplicateOpen(false)}
        onConfirm={() => (deletingContact ? handleDuplicateFlow(deletingContact.id) : null)}
      >
        {deletingContact
          ? `Tem certeza que deseja duplicar este fluxo?`
          : `${i18n.t("contacts.confirmationModal.importMessage")}`}
      </ConfirmationModal>
    </Box>
  );
};

export default FlowBuilder;